import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faLock,
  faEye,
  faEyeSlash,
  faLock as faLockIcon,
  faShieldAlt,
  faCloud,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../utils/Url";
import useLocalStorage from "use-local-storage";
import {
  Button,
  CircularProgress,
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import * as Yup from "yup";
import advPics from "../assets/images/headDoc.png";
import logo from "../assets/images/Login.png";
import { keyframes } from "@emotion/react";
import { secureSetItem, encryptData } from "../utils/encryption";
import SplashScreen from "./SplashScreen";

function LoginPage() {
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [unlockTime, setUnlockTime] = useState<Date | null>(null);
  const [remainingTime, setRemainingTime] = useState<{ minutes: number; seconds: number } | null>(null);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(false);
  const [imageHover, setImageHover] = useState(false);
  const [leftCardHover, setLeftCardHover] = useState(false);
  const [rightCardHover, setRightCardHover] = useState(false);
  const [isUsernameFocused, setIsUsernameFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isCaptchaFocused, setIsCaptchaFocused] = useState(false);
  
  // Simple CAPTCHA state
  const [captchaCode, setCaptchaCode] = useState("");
  const [userCaptchaInput, setUserCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaTouched, setCaptchaTouched] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Generate random CAPTCHA (always uppercase)
const generateCaptcha = () => {
  const characters = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  setCaptchaCode(result);
  setUserCaptchaInput("");
  setCaptchaError("");
  setCaptchaTouched(false);

  setTimeout(() => drawCaptcha(result), 100);
};

  const drawCaptcha = (text: string) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Background
  ctx.fillStyle = "#e3f2fd";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Random noise lines
  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = `rgba(0,0,0,${Math.random()})`;
    ctx.beginPath();
    ctx.moveTo(Math.random() * 200, Math.random() * 50);
    ctx.lineTo(Math.random() * 200, Math.random() * 50);
    ctx.stroke();
  }

  // Draw text (distorted)
  ctx.font = "bold 28px monospace";
  ctx.fillStyle = "#1976d2";

  for (let i = 0; i < text.length; i++) {
    const x = 20 + i * 25;
    const y = 35;

    const angle = (Math.random() - 0.5) * 0.6;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillText(text[i], 0, 0);
    ctx.restore();
  }
};

  // Generate CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Countdown timer effect (unchanged)
  useEffect(() => {
    if (unlockTime) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const diff = unlockTime.getTime() - now;
        if (diff <= 0) {
          clearInterval(interval);
          setRemainingAttempts(null);
          setUnlockTime(null);
          setRemainingTime(null);
          setIsAccountLocked(false);
        } else {
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setRemainingTime({ minutes, seconds });
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [unlockTime]);

  const parseUnlockTime = (lockTimeStrStr: string): Date => {
    const timeMatch = lockTimeStrStr.match(/(\d{1,2}):(\d{2}) ([AP]M)/i);
    if (timeMatch) {
      const now = new Date();
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const ampm = timeMatch[3].toUpperCase();
      if (ampm === "PM" && hours !== 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      const unlock = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0);
      if (unlock < now) unlock.setDate(unlock.getDate() + 1);
      return unlock;
    } else {
      const durationMatch = lockTimeStrStr.match(/(\d+)\s*minutes?/i);
      if (durationMatch) {
        const min = parseInt(durationMatch[1]);
        return new Date(new Date().getTime() + min * 60000);
      }
    }
    return new Date(new Date().getTime() + 15 * 60000);
  };

  const validationSchema = Yup.object({
    useR_ID: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      useR_ID: "",
      password: "",
      collageID: "1",
      packageID: "1",
    },
    validationSchema,
    onSubmit: () => {},
  });

  // Validate CAPTCHA
  const validateCaptcha = () => {
    if (!userCaptchaInput || userCaptchaInput.trim() === "") {
      setCaptchaError("Verification code is required");
      return false;
    }
    if (userCaptchaInput.toUpperCase() !== captchaCode) {
      setCaptchaError("Invalid verification code");
      return false;
    }
    setCaptchaError("");
    return true;
  };

  // Login handler with CAPTCHA validation
  const handleButtonClick = async () => {
    setCaptchaTouched(true);
    
    // Validate CAPTCHA first
    if (!validateCaptcha()) {
      return;
    }

    if (!loading) {
      setLoading(true);
      let hasError = false;
      
      // Trigger validation first
      const errors = await formik.validateForm();
      
      // Check if there are validation errors
      if (Object.keys(errors).length > 0) {
        // Set touched fields to show errors
        const touched: any = {};
        Object.keys(errors).forEach(key => {
          touched[key] = true;
        });
        formik.setTouched(touched);
        
        setLoading(false);
        return; // Stop execution here
      }

      try {
        // Only proceed if validation passes
        const encryptedPayload = {
          useR_ID: encryptData(formik.values.useR_ID),
          password: encryptData(formik.values.password),
          collageID: formik.values.collageID,
          packageID: formik.values.packageID,
        };

        const response = await axios.post(HOST_URL.HOST_URL + `USER/USERLOGIN`, encryptedPayload);

        if (response.data.isSuccess) {
          if (response.data.data[0]["userPermission"] == null) {
            toast.error("User Permission is null");
            hasError = true;
          } else {
            secureSetItem("userdata", response.data.data);
            secureSetItem("useR_ID", response.data.data[0]["userdetail"][0]["useR_ID"]);
            secureSetItem("devID", response.data.data[0]["userdetail"][0]["departmentId"]);
            secureSetItem("devName", response.data.data[0]["userdetail"][0]["department_Name"]);
            sessionStorage.setItem("token", response.data.data[0]["token"]);

            setShowSplash(true);
            setTimeout(() => {
              navigate(`/home`);
              toast.success(response.data.mesg || "Login successful");
              formik.resetForm();
              setRemainingAttempts(null);
              setUnlockTime(null);
              setIsAccountLocked(false);
              setShowSplash(false);
              generateCaptcha(); // Generate new CAPTCHA after successful login
            }, 3000);
          }
        } else {
          hasError = true;
          const errorMessage = response.data.mesg || "Login failed";

          if (errorMessage === "Invalid credentials") {
            setRemainingAttempts((prev) => {
              const newAttempts = prev !== null ? Math.max(0, prev - 1) : 4;
              toast.error(`Invalid credentials. You have ${newAttempts} attempt${newAttempts !== 1 ? "s" : ""} remaining`);
              return newAttempts;
            });
          } else if (errorMessage.includes("attempt") || errorMessage.includes("attempts")) {
            const attemptsMatch = errorMessage.match(/(\d+)\s+attempt/);
            if (attemptsMatch && attemptsMatch[1]) {
              setRemainingAttempts(parseInt(attemptsMatch[1]));
            } else {
              setRemainingAttempts((prev) => (prev !== null ? Math.max(0, prev - 1) : 4));
            }
            toast.error(errorMessage);
          } else if (errorMessage.includes("locked") || errorMessage.includes("Try again after")) {
            const timeMatch = errorMessage.match(/Try again after\s+(.*)$/);
            const lockTimeStr = timeMatch ? timeMatch[1] : "15 minutes";
            const parsedUnlockTime = parseUnlockTime(lockTimeStr);
            setUnlockTime(parsedUnlockTime);
            setIsAccountLocked(true);
            toast.error(errorMessage);
            setRemainingAttempts(0);
          } else {
            toast.error(errorMessage);
          }
          setLoading(false);
          generateCaptcha(); // Generate new CAPTCHA on failed login
        }
      } catch (error: any) {
        hasError = true;
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          const errorMessage = errorData.mesg || "Login Failed";

          if (errorMessage === "Invalid credentials") {
            setRemainingAttempts((prev) => {
              const newAttempts = prev !== null ? Math.max(0, prev - 1) : 4;
              toast.error(`Invalid credentials. You have ${newAttempts} attempt${newAttempts !== 1 ? "s" : ""} remaining`);
              return newAttempts;
            });
          } else if (errorMessage.includes("attempt") || errorMessage.includes("attempts")) {
            const attemptsMatch = errorMessage.match(/(\d+)\s+attempt/);
            if (attemptsMatch && attemptsMatch[1]) {
              setRemainingAttempts(parseInt(attemptsMatch[1]));
            }
            toast.error(errorMessage);
          } else if (errorMessage.includes("locked") || errorMessage.includes("Try again after")) {
            const timeMatch = errorMessage.match(/Try again after\s+(.*)$/);
            const lockTimeStr = timeMatch ? timeMatch[1] : "15 minutes";
            const parsedUnlockTime = parseUnlockTime(lockTimeStr);
            setUnlockTime(parsedUnlockTime);
            setIsAccountLocked(true);
            toast.error(errorMessage);
            setRemainingAttempts(0);
          } else {
            toast.error(errorMessage);
          }
        } else {
          toast.error("Login Failed");
        }
        setLoading(false);
        generateCaptcha(); // Generate new CAPTCHA on error
      } finally {
        if (hasError) {
          // Reset CAPTCHA on error
        }
      }
    }
  };

  // Handle CAPTCHA input change
  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserCaptchaInput(e.target.value.toUpperCase()); // Convert to uppercase
    if (captchaError) {
      setCaptchaError("");
    }
  };

  // Handle CAPTCHA blur
  const handleCaptchaBlur = () => {
    setCaptchaTouched(true);
    validateCaptcha();
  };

  // Attempts message
  const AttemptsMessage = () => {
    if (remainingAttempts === null || remainingAttempts === 0) return null;

    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 1,
          py: 0.5,
          px: 1,
          borderRadius: "20px",
          bgcolor:
            remainingAttempts > 2
              ? "#e8f5e8"
              : remainingAttempts > 1
              ? "#fff3cd"
              : "#f8d7da",
          color:
            remainingAttempts > 2
              ? "#2e7d32"
              : remainingAttempts > 1
              ? "#856404"
              : "#721c24",
          fontSize: "0.75rem",
          fontWeight: 500,
          animation: "pulse 1.5s infinite",
        }}
      >
        <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 4, fontSize: "0.7rem" }} />
        {`You have ${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining`}
      </Box>
    );
  };

  // Locked screen (unchanged)
  const LockedScreen = () => (
    <Box sx={{ textAlign: "center", p: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          bgcolor: "#d32f2f",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 1,
          boxShadow: "0 8px 16px rgba(211,47,47,0.3)",
        }}
      >
        <FontAwesomeIcon icon={faLockIcon} size="lg" color="white" />
      </Box>
      <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: 600 }}>
        Account Locked
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
        Multiple failed attempts
      </Typography>
      {remainingTime && (
        <Typography variant="caption" sx={{ color: "#d32f2f", fontWeight: 500 }}>
          Try again in {remainingTime.minutes}:{remainingTime.seconds.toString().padStart(2, "0")}
        </Typography>
      )}
    </Box>
  );

  // Animations
  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  `;

  const glow = keyframes`
    0%, 100% { text-shadow: 0 0 10px rgba(33,150,243,0.3); }
    50% { text-shadow: 0 0 20px rgba(33,150,243,0.6); }
  `;

  const rotateIn = keyframes`
    from { opacity: 0; transform: perspective(400px) rotateX(10deg) translateY(20px); }
    to { opacity: 1; transform: perspective(400px) rotateX(0) translateY(0); }
  `;

  return (
    <>
      {showSplash && <SplashScreen duration={3000} onComplete={() => setShowSplash(false)} />}

      <Container maxWidth={false} sx={{ minHeight: "100vh", display: "flex", alignItems: "center", p: 0 }}>
        <ToastContainer position="top-right" autoClose={3000} theme="colored" />

        <Grid container sx={{ minHeight: "100vh" }}>
          {/* Left side - Features & Illustration with 3D effect */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "linear-gradient(145deg, #f0f4ff 0%, #e6eeff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 3, md: 4 },
              perspective: "1000px",
              position: "relative",
              overflow: "hidden",
              '&::before': {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 20% 50%, rgba(33,150,243,0.1) 0%, transparent 50%)",
                pointerEvents: "none",
              },
            }}
          >
            <Box
              sx={{
                maxWidth: 500,
                animation: `${rotateIn} 0.8s ease`,
                transform: leftCardHover
                  ? "rotateY(2deg) rotateX(2deg) scale(1.02)"
                  : "rotateY(0) rotateX(0) scale(1)",
                transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                transformStyle: "preserve-3d",
                boxShadow: leftCardHover
                  ? "0 30px 60px rgba(33,150,243,0.4), 0 0 0 2px rgba(33,150,243,0.1)"
                  : "0 20px 40px rgba(0,0,0,0.1)",
                borderRadius: "24px",
                p: 3,
                bgcolor: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.2)",
                position: "relative",
                zIndex: 2,
              }}
              onMouseEnter={() => setLeftCardHover(true)}
              onMouseLeave={() => setLeftCardHover(false)}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  color: "#1976d2",
                  mb: 0.5,
                  animation: `${fadeIn} 0.6s ease`,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  letterSpacing: "0.5px",
                }}
              >
                Welcome to
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.6rem", md: "1.8rem" },
                  color: "#0a1929",
                  mb: 1.5,
                  pb:1,
                  animation: `${glow} 3s infinite`,
                  lineHeight: 1.2,
                  background: "linear-gradient(135deg, #0a1929 0%, #1a2b4a 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {/* Document Management System */}
                Institutional Digital Repository Software
              </Typography>

              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#2d3a5e", 
                  mb: 2, 
                  fontSize: { xs: "0.85rem", md: "0.9rem" },
                  lineHeight: 1.6,
                }}
              >
                Organize, collaborate, and secure your documents with enterprise-grade protection
                and AI-powered workflows.
              </Typography>

              {/* Feature list with icons */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                {[
                  { icon: faShieldAlt, title: "Bank-grade encryption", desc: "Your data is protected with AES-256" },
                  { icon: faCloud, title: "Cloud sync", desc: "Access from anywhere, anytime" },
                ].map((feature, idx) => (
                  <Box 
                    key={idx} 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1.5,
                      transition: "transform 0.3s ease",
                      '&:hover': {
                        transform: "translateX(5px)",
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #2196f3, #1976d2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 6px 12px rgba(33,150,243,0.3)",
                      }}
                    >
                      <FontAwesomeIcon icon={feature.icon} color="white" size="sm" />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0a1929", fontSize: "0.9rem" }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#4a5a7a", fontSize: "0.75rem" }}>
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Illustration with 3D hover */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  animation: `${float} 4s ease-in-out infinite`,
                  transform: imageHover
                    ? "perspective(500px) rotateY(5deg) rotateX(5deg) scale(1.05)"
                    : "perspective(500px) rotateY(0) rotateX(0) scale(1)",
                  transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  cursor: "pointer",
                  filter: imageHover ? "drop-shadow(0 30px 40px rgba(33,150,243,0.5))" : "drop-shadow(0 20px 30px rgba(33,150,243,0.3))",
                }}
                onMouseEnter={() => setImageHover(true)}
                onMouseLeave={() => setImageHover(false)}
              >
                <img
                  src={advPics}
                  alt="Document Management"
                  style={{
                    maxWidth: "70%",
                    height: "auto",
                    maxHeight: 180,
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right side - Login Form with 3D effect */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, md: 4 },
              bgcolor: "white",
              perspective: "1000px",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 400,
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                animation: `${fadeIn} 0.6s ease`,
                transform: rightCardHover
                  ? "rotateY(-2deg) rotateX(1deg) scale(1.02)"
                  : "rotateY(0) rotateX(0) scale(1)",
                transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                bgcolor: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.2)",
                position: "relative",
                '&::after': {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 4,
                  background: "linear-gradient(135deg, rgba(33,150,243,0.1), transparent)",
                  pointerEvents: "none",
                },
              }}
              onMouseEnter={() => setRightCardHover(true)}
              onMouseLeave={() => setRightCardHover(false)}
            >
              {/* Logo and Title */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: "50%",
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 20px rgba(33,150,243,0.2)",
                    mb: 1,
                  }}
                >
                  <img src={logo} alt="Logo" style={{ height: 50, width: "auto" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#0a1929",
                    animation: `${glow} 3s infinite`,
                    textAlign: "center",
                    lineHeight: 1.3,
                    pb: 1,
                    fontSize: "1.1rem",
                  }}
                >
                  {/* Document Management System */}
                  Institutional Digital Repository Software
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: "#4a5a7a", mb: 3, textAlign: "center", fontSize: "0.85rem" }}>
                Sign in to your document workspace
              </Typography>

              {isAccountLocked ? (
                <LockedScreen />
              ) : (
              <form onSubmit={(e) => {
  e.preventDefault();
  handleButtonClick();
}}>
                  {/* Username field */}
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 500, 
                        color: isUsernameFocused ? "#2196f3" : "#0a1929", 
                        mb: 0.5, 
                        display: "block",
                        transition: "color 0.2s",
                      }}
                    >
                      Username
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#f5f7fc",
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        border: "2px solid transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:focus-within": {
                          borderColor: "#2196f3",
                          bgcolor: "white",
                          boxShadow: "0 0 0 3px rgba(33,150,243,0.2)",
                          transform: "scale(1.02)",
                        },
                      }}
                    >
                      <FontAwesomeIcon icon={faUser} style={{ color: "#2196f3", marginRight: 8, fontSize: 14 }} />
                      <input
                        type="text"
                        name="useR_ID"
                        value={formik.values.useR_ID}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          setIsUsernameFocused(false);
                        }}
                        onFocus={() => setIsUsernameFocused(true)}
                        placeholder="Username"
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "0.9rem",
                          padding: "4px 0",
                        }}
                      />
                    </Box>
                    {formik.touched.useR_ID && formik.errors.useR_ID && (
                      <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1, mt: 0.5, display: "block" }}>
                        ⚠ {formik.errors.useR_ID}
                      </Typography>
                    )}
                  </Box>

                  {/* Password field */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 500, 
                          color: isPasswordFocused ? "#2196f3" : "#0a1929",
                          transition: "color 0.2s",
                        }}
                      >
                        Password
                      </Typography>
                    
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#f5f7fc",
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        border: "2px solid transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:focus-within": {
                          borderColor: "#2196f3",
                          bgcolor: "white",
                          boxShadow: "0 0 0 3px rgba(33,150,243,0.2)",
                          transform: "scale(1.02)",
                        },
                      }}
                    >
                      <FontAwesomeIcon icon={faLock} style={{ color: "#2196f3", marginRight: 8, fontSize: 14 }} />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          setIsPasswordFocused(false);
                        }}
                        onFocus={() => setIsPasswordFocused(true)}
                        placeholder="Password"
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "0.9rem",
                          padding: "4px 0",
                        }}
                      />
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        style={{ color: "#2196f3", fontSize: 14, cursor: "pointer" }}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </Box>
                    {formik.touched.password && formik.errors.password && (
                      <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1, mt: 0.5, display: "block" }}>
                        ⚠ {formik.errors.password}
                      </Typography>
                    )}
                  </Box>

                  {/* Simple Text CAPTCHA */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 500, 
                          color: isCaptchaFocused ? "#2196f3" : "#0a1929",
                          transition: "color 0.2s",
                        }}
                      >
                        Verification Code
                      </Typography>
                      <Box 
                        onClick={generateCaptcha}
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 0.5,
                          cursor: "pointer",
                          color: "#2196f3",
                          fontSize: "0.75rem",
                          '&:hover': { opacity: 0.8 }
                        }}
                      >
                        <FontAwesomeIcon icon={faRefresh} size="xs" />
                        <span>Refresh</span>
                      </Box>
                    </Box>
                    
                    {/* CAPTCHA Display - Always Uppercase */}
                    {/* <Box
                      sx={{
                        bgcolor: "#f0f4ff",
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                        mb: 1,
                        border: "2px dashed #2196f3",
                        textAlign: "center",
                        fontFamily: "monospace",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        letterSpacing: "4px",
                        color: "#1976d2",
                        background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                        textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
                      }}
                    >
                      {captchaCode}
                    </Box> */}


                    <canvas
  ref={canvasRef}
  width={200}
  height={50}
  style={{
    borderRadius: 8,
    border: "2px dashed #2196f3",
    marginBottom: 8,
    background: "#f0f4ff",
  }}
/>

                    {/* CAPTCHA Input */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#f5f7fc",
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        border: "2px solid transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:focus-within": {
                          borderColor: "#2196f3",
                          bgcolor: "white",
                          boxShadow: "0 0 0 3px rgba(33,150,243,0.2)",
                          transform: "scale(1.02)",
                        },
                      }}
                    >
                      <input
                        type="text"
                        value={userCaptchaInput}
                        onChange={handleCaptchaChange}
                        onFocus={() => setIsCaptchaFocused(true)}
                        onBlur={handleCaptchaBlur}
                        placeholder="Enter code above"
                        maxLength={6}
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "0.9rem",
                          padding: "4px 0",
                          textTransform: "uppercase",
                        }}
                      />
                    </Box>
                    {(captchaTouched && !userCaptchaInput) ? (
                      <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1, mt: 0.5, display: "block" }}>
                        ⚠ Verification code is required
                      </Typography>
                    ) : captchaError ? (
                      <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1, mt: 0.5, display: "block" }}>
                        ⚠ {captchaError}
                      </Typography>
                    ) : null}
                  </Box>

                  {/* Sign In Button - Blue */}
                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    onClick={handleButtonClick}
                    sx={{
                      background: "linear-gradient(135deg, #2196f3, #1976d2)",
                      color: "white",
                      py: 1.2,
                      borderRadius: 2,
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow: "0 8px 16px rgba(33,150,243,0.4)",
                      position: "relative",
                      overflow: "hidden",
                      '&::before': {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                        transition: "left 0.5s ease",
                      },
                      "&:hover": {
                        transform: "translateY(-2px) scale(1.02)",
                        boxShadow: "0 12px 24px rgba(33,150,243,0.6)",
                        '&::before': {
                          left: "100%",
                        },
                      },
                      "&:disabled": { opacity: 0.7 },
                      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      mb: 2,
                    }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Sign in →"}
                  </Button>

                  {/* Attempts message */}
                  <AttemptsMessage />
                </form>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default LoginPage;


// import React, { useState, useRef, useEffect } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faUser,
//   faLock,
//   faEye,
//   faEyeSlash,
//   faLock as faLockIcon,
//   faShieldAlt,
//   faCloud,
// } from "@fortawesome/free-solid-svg-icons";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import HOST_URL from "../utils/Url";
// import useLocalStorage from "use-local-storage";
// import {
//   Button,
//   CircularProgress,
//   Grid,
//   Box,
//   Container,
//   useMediaQuery,
//   useTheme,
//   Typography,
// } from "@mui/material";
// import { useFormik } from "formik";
// import { toast, ToastContainer } from "react-toastify";
// import * as Yup from "yup";
// import advPics from "../assets/images/headDoc.png";
// import logo from "../assets/images/Login.png";
// import { keyframes } from "@emotion/react";
// import { secureSetItem, encryptData } from "../utils/encryption";
// import ReCAPTCHA from "react-google-recaptcha";
// import SplashScreen from "./SplashScreen";

// function LoginPage() {
//   const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
//   const [loading, setLoading] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const [unlockTime, setUnlockTime] = useState<Date | null>(null);
//   const [remainingTime, setRemainingTime] = useState<{ minutes: number; seconds: number } | null>(null);
//   const [isAccountLocked, setIsAccountLocked] = useState(false);
//   const timer = useRef<ReturnType<typeof setTimeout>>();
//   const recaptchaRef = useRef<ReCAPTCHA>(null);
//   const navigate = useNavigate();
//   const [showSplash, setShowSplash] = useState(false);
//   const [imageHover, setImageHover] = useState(false);
//   const [leftCardHover, setLeftCardHover] = useState(false);
//   const [rightCardHover, setRightCardHover] = useState(false);
//   const [isUsernameFocused, setIsUsernameFocused] = useState(false);
//   const [isPasswordFocused, setIsPasswordFocused] = useState(false);

//   // Countdown timer effect (unchanged)
//   useEffect(() => {
//     if (unlockTime) {
//       const interval = setInterval(() => {
//         const now = new Date().getTime();
//         const diff = unlockTime.getTime() - now;
//         if (diff <= 0) {
//           clearInterval(interval);
//           setRemainingAttempts(null);
//           setUnlockTime(null);
//           setRemainingTime(null);
//           setIsAccountLocked(false);
//         } else {
//           const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
//           const seconds = Math.floor((diff % (1000 * 60)) / 1000);
//           setRemainingTime({ minutes, seconds });
//         }
//       }, 1000);
//       return () => clearInterval(interval);
//     }
//   }, [unlockTime]);

//   const parseUnlockTime = (lockTimeStrStr: string): Date => {
//     const timeMatch = lockTimeStrStr.match(/(\d{1,2}):(\d{2}) ([AP]M)/i);
//     if (timeMatch) {
//       const now = new Date();
//       let hours = parseInt(timeMatch[1]);
//       const minutes = parseInt(timeMatch[2]);
//       const ampm = timeMatch[3].toUpperCase();
//       if (ampm === "PM" && hours !== 12) hours += 12;
//       if (ampm === "AM" && hours === 12) hours = 0;
//       const unlock = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0);
//       if (unlock < now) unlock.setDate(unlock.getDate() + 1);
//       return unlock;
//     } else {
//       const durationMatch = lockTimeStrStr.match(/(\d+)\s*minutes?/i);
//       if (durationMatch) {
//         const min = parseInt(durationMatch[1]);
//         return new Date(new Date().getTime() + min * 60000);
//       }
//     }
//     return new Date(new Date().getTime() + 15 * 60000);
//   };

//   const validationSchema = Yup.object({
//     useR_ID: Yup.string().required("Username is required"),
//     password: Yup.string().required("Password is required"),
//   });

//   const formik = useFormik({
//     initialValues: {
//       useR_ID: "",
//       password: "",
//       collageID: "1",
//       packageID: "1",
//       gRecaptchaResponse: "",
//     },
//     validationSchema,
//     onSubmit: () => {},
//   });

//   const handleCaptchaChange = (token: string | null) => {
//     formik.setFieldValue("gRecaptchaResponse", token || "");
//   };

//   const handleCaptchaExpired = () => {
//     formik.setFieldValue("gRecaptchaResponse", "");
//   };

//   const resetCaptcha = () => {
//     recaptchaRef.current?.reset();
//     formik.setFieldValue("gRecaptchaResponse", "");
//   };

//   // Login handler (unchanged logic)

// const handleButtonClick = async () => {
//   if (!loading) {
//     setLoading(true);
//     let hasError = false;
    
//     // Trigger validation first
//     const errors = await formik.validateForm();
    
//     // Check if there are validation errors
//     if (Object.keys(errors).length > 0) {
//       // Set touched fields to show errors
//       const touched: any = {};
//       Object.keys(errors).forEach(key => {
//         touched[key] = true;
//       });
//       formik.setTouched(touched);
      
   
//       setLoading(false);
//       return; // Stop execution here
//     }

//     try {
//       // Only proceed if validation passes
//       const encryptedPayload = {
//         useR_ID: encryptData(formik.values.useR_ID),
//         password: encryptData(formik.values.password),
//         collageID: formik.values.collageID,
//         packageID: formik.values.packageID,
//         gRecaptchaResponse: formik.values.gRecaptchaResponse,
//       };

//       const response = await axios.post(HOST_URL.HOST_URL + `USER/USERLOGIN`, encryptedPayload);

//       if (response.data.isSuccess) {
//         if (response.data.data[0]["userPermission"] == null) {
//           toast.error("User Permission is null");
//           hasError = true;
//         } else {
//           secureSetItem("userdata", response.data.data);
//           secureSetItem("useR_ID", response.data.data[0]["userdetail"][0]["useR_ID"]);
//           secureSetItem("devID", response.data.data[0]["userdetail"][0]["departmentId"]);
//           secureSetItem("devName", response.data.data[0]["userdetail"][0]["department_Name"]);
//           sessionStorage.setItem("token", response.data.data[0]["token"]);

//           setShowSplash(true);
//           setTimeout(() => {
//             navigate(`/home`);
//             toast.success(response.data.mesg || "Login successful");
//             formik.resetForm();
//             setRemainingAttempts(null);
//             setUnlockTime(null);
//             setIsAccountLocked(false);
//             setShowSplash(false);
//           }, 3000);
//         }
//       } else {
//         hasError = true;
//         const errorMessage = response.data.mesg || "Login failed";

//         if (errorMessage === "Invalid credentials") {
//           setRemainingAttempts((prev) => {
//             const newAttempts = prev !== null ? Math.max(0, prev - 1) : 4;
//             toast.error(`Invalid credentials. You have ${newAttempts} attempt${newAttempts !== 1 ? "s" : ""} remaining`);
//             return newAttempts;
//           });
//         } else if (errorMessage.includes("attempt") || errorMessage.includes("attempts")) {
//           const attemptsMatch = errorMessage.match(/(\d+)\s+attempt/);
//           if (attemptsMatch && attemptsMatch[1]) {
//             setRemainingAttempts(parseInt(attemptsMatch[1]));
//           } else {
//             setRemainingAttempts((prev) => (prev !== null ? Math.max(0, prev - 1) : 4));
//           }
//           toast.error(errorMessage);
//         } else if (errorMessage.includes("locked") || errorMessage.includes("Try again after")) {
//           const timeMatch = errorMessage.match(/Try again after\s+(.*)$/);
//           const lockTimeStr = timeMatch ? timeMatch[1] : "15 minutes";
//           const parsedUnlockTime = parseUnlockTime(lockTimeStr);
//           setUnlockTime(parsedUnlockTime);
//           setIsAccountLocked(true);
//           toast.error(errorMessage);
//           setRemainingAttempts(0);
//         } else {
//           toast.error(errorMessage);
//         }
//         setLoading(false);
//       }
//     } catch (error: any) {
//       hasError = true;
//       if (error.response && error.response.data) {
//         const errorData = error.response.data;
//         const errorMessage = errorData.mesg || "Login Failed";

//         if (errorMessage === "Invalid credentials") {
//           setRemainingAttempts((prev) => {
//             const newAttempts = prev !== null ? Math.max(0, prev - 1) : 4;
//             toast.error(`Invalid credentials. You have ${newAttempts} attempt${newAttempts !== 1 ? "s" : ""} remaining`);
//             return newAttempts;
//           });
//         } else if (errorMessage.includes("attempt") || errorMessage.includes("attempts")) {
//           const attemptsMatch = errorMessage.match(/(\d+)\s+attempt/);
//           if (attemptsMatch && attemptsMatch[1]) {
//             setRemainingAttempts(parseInt(attemptsMatch[1]));
//           }
//           toast.error(errorMessage);
//         } else if (errorMessage.includes("locked") || errorMessage.includes("Try again after")) {
//           const timeMatch = errorMessage.match(/Try again after\s+(.*)$/);
//           const lockTimeStr = timeMatch ? timeMatch[1] : "15 minutes";
//           const parsedUnlockTime = parseUnlockTime(lockTimeStr);
//           setUnlockTime(parsedUnlockTime);
//           setIsAccountLocked(true);
//           toast.error(errorMessage);
//           setRemainingAttempts(0);
//         } else {
//           toast.error(errorMessage);
//         }
//       } else {
//         toast.error("Login Failed");
//       }
//       setLoading(false);
//     } finally {
//       if (hasError) {
//         resetCaptcha();
//       }
//     }
//   }
// };



//   // Attempts message
//   const AttemptsMessage = () => {
//     if (remainingAttempts === null || remainingAttempts === 0) return null;

//     return (
//       <Box
//         sx={{
//           textAlign: "center",
//           mt: 1,
//           py: 0.5,
//           px: 1,
//           borderRadius: "20px",
//           bgcolor:
//             remainingAttempts > 2
//               ? "#e8f5e8"
//               : remainingAttempts > 1
//               ? "#fff3cd"
//               : "#f8d7da",
//           color:
//             remainingAttempts > 2
//               ? "#2e7d32"
//               : remainingAttempts > 1
//               ? "#856404"
//               : "#721c24",
//           fontSize: "0.75rem",
//           fontWeight: 500,
//           animation: "pulse 1.5s infinite",
//         }}
//       >
//         <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 4, fontSize: "0.7rem" }} />
//         {`You have ${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining`}
//       </Box>
//     );
//   };

//   // Locked screen (unchanged)
//   const LockedScreen = () => (
//     <Box sx={{ textAlign: "center", p: 2 }}>
//       <Box
//         sx={{
//           width: 48,
//           height: 48,
//           borderRadius: "50%",
//           bgcolor: "#d32f2f",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           mx: "auto",
//           mb: 1,
//           boxShadow: "0 8px 16px rgba(211,47,47,0.3)",
//         }}
//       >
//         <FontAwesomeIcon icon={faLockIcon} size="lg" color="white" />
//       </Box>
//       <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: 600 }}>
//         Account Locked
//       </Typography>
//       <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
//         Multiple failed attempts
//       </Typography>
//       {remainingTime && (
//         <Typography variant="caption" sx={{ color: "#d32f2f", fontWeight: 500 }}>
//           Try again in {remainingTime.minutes}:{remainingTime.seconds.toString().padStart(2, "0")}
//         </Typography>
//       )}
//     </Box>
//   );

//   // Animations
//   const fadeIn = keyframes`
//     from { opacity: 0; transform: translateY(10px); }
//     to { opacity: 1; transform: translateY(0); }
//   `;

//   const float = keyframes`
//     0%, 100% { transform: translateY(0px); }
//     50% { transform: translateY(-8px); }
//   `;

//   const glow = keyframes`
//     0%, 100% { text-shadow: 0 0 10px rgba(33,150,243,0.3); }
//     50% { text-shadow: 0 0 20px rgba(33,150,243,0.6); }
//   `;

//   const rotateIn = keyframes`
//     from { opacity: 0; transform: perspective(400px) rotateX(10deg) translateY(20px); }
//     to { opacity: 1; transform: perspective(400px) rotateX(0) translateY(0); }
//   `;

//   const pulse = keyframes`
//     0%, 100% { box-shadow: 0 0 0 0 rgba(33,150,243,0.4); }
//     50% { box-shadow: 0 0 0 5px rgba(33,150,243,0); }
//   `;

//   const shimmer = keyframes`
//     0% { background-position: -1000px 0; }
//     100% { background-position: 1000px 0; }
//   `;

//   return (
//     <>
//       {showSplash && <SplashScreen duration={3000} onComplete={() => setShowSplash(false)} />}

//       <Container maxWidth={false} sx={{ minHeight: "100vh", display: "flex", alignItems: "center", p: 0 }}>
//         <ToastContainer position="top-right" autoClose={3000} theme="colored" />

//         <Grid container sx={{ minHeight: "100vh" }}>
//           {/* Left side - Features & Illustration with 3D effect */}
//           <Grid
//             item
//             xs={12}
//             md={6}
//             sx={{
//               background: "linear-gradient(145deg, #f0f4ff 0%, #e6eeff 100%)",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               p: { xs: 3, md: 4 },
//               perspective: "1000px",
//               position: "relative",
//               overflow: "hidden",
//               '&::before': {
//                 content: '""',
//                 position: "absolute",
//                 top: 0,
//                 left: 0,
//                 right: 0,
//                 bottom: 0,
//                 background: "radial-gradient(circle at 20% 50%, rgba(33,150,243,0.1) 0%, transparent 50%)",
//                 pointerEvents: "none",
//               },
//             }}
//           >
//             <Box
//               sx={{
//                 maxWidth: 500,
//                 animation: `${rotateIn} 0.8s ease`,
//                 transform: leftCardHover
//                   ? "rotateY(2deg) rotateX(2deg) scale(1.02)"
//                   : "rotateY(0) rotateX(0) scale(1)",
//                 transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
//                 transformStyle: "preserve-3d",
//                 boxShadow: leftCardHover
//                   ? "0 30px 60px rgba(33,150,243,0.4), 0 0 0 2px rgba(33,150,243,0.1)"
//                   : "0 20px 40px rgba(0,0,0,0.1)",
//                 borderRadius: "24px",
//                 p: 3,
//                 bgcolor: "rgba(255,255,255,0.7)",
//                 backdropFilter: "blur(10px)",
//                 border: "1px solid rgba(255,255,255,0.2)",
//                 position: "relative",
//                 zIndex: 2,
//               }}
//               onMouseEnter={() => setLeftCardHover(true)}
//               onMouseLeave={() => setLeftCardHover(false)}
//             >
//               <Typography
//                 variant="h6"
//                 sx={{
//                   fontWeight: 400,
//                   color: "#1976d2",
//                   mb: 0.5,
//                   animation: `${fadeIn} 0.6s ease`,
//                   fontSize: { xs: "1rem", md: "1.1rem" },
//                   letterSpacing: "0.5px",
//                 }}
//               >
//                 Welcome to
//               </Typography>
//               <Typography
//                 variant="h4"
//                 sx={{
//                   fontWeight: 700,
//                   fontSize: { xs: "1.6rem", md: "1.8rem" },
//                   color: "#0a1929",
//                   mb: 1.5,
//                   pb:1,
//                   animation: `${glow} 3s infinite`,
//                   lineHeight: 1.2,
//                   background: "linear-gradient(135deg, #0a1929 0%, #1a2b4a 100%)",
//                   WebkitBackgroundClip: "text",
//                   WebkitTextFillColor: "transparent",
//                 }}
//               >
//                 Document Management System
//               </Typography>

//               <Typography 
//                 variant="body2" 
//                 sx={{ 
//                   color: "#2d3a5e", 
//                   mb: 2, 
//                   fontSize: { xs: "0.85rem", md: "0.9rem" },
//                   lineHeight: 1.6,
//                 }}
//               >
//                 Organize, collaborate, and secure your documents with enterprise-grade protection
//                 and AI-powered workflows.
//               </Typography>

//               {/* Feature list with icons */}
//               <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
//                 {[
//                   { icon: faShieldAlt, title: "Bank-grade encryption", desc: "Your data is protected with AES-256" },
//                   { icon: faCloud, title: "Cloud sync", desc: "Access from anywhere, anytime" },
//                 ].map((feature, idx) => (
//                   <Box 
//                     key={idx} 
//                     sx={{ 
//                       display: "flex", 
//                       alignItems: "center", 
//                       gap: 1.5,
//                       transition: "transform 0.3s ease",
//                       '&:hover': {
//                         transform: "translateX(5px)",
//                       }
//                     }}
//                   >
//                     <Box
//                       sx={{
//                         width: 40,
//                         height: 40,
//                         borderRadius: "12px",
//                         background: "linear-gradient(135deg, #2196f3, #1976d2)",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         boxShadow: "0 6px 12px rgba(33,150,243,0.3)",
//                       }}
//                     >
//                       <FontAwesomeIcon icon={feature.icon} color="white" size="sm" />
//                     </Box>
//                     <Box>
//                       <Typography variant="body2" sx={{ fontWeight: 600, color: "#0a1929", fontSize: "0.9rem" }}>
//                         {feature.title}
//                       </Typography>
//                       <Typography variant="caption" sx={{ color: "#4a5a7a", fontSize: "0.75rem" }}>
//                         {feature.desc}
//                       </Typography>
//                     </Box>
//                   </Box>
//                 ))}
//               </Box>

//               {/* Illustration with 3D hover */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   animation: `${float} 4s ease-in-out infinite`,
//                   transform: imageHover
//                     ? "perspective(500px) rotateY(5deg) rotateX(5deg) scale(1.05)"
//                     : "perspective(500px) rotateY(0) rotateX(0) scale(1)",
//                   transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
//                   cursor: "pointer",
//                   filter: imageHover ? "drop-shadow(0 30px 40px rgba(33,150,243,0.5))" : "drop-shadow(0 20px 30px rgba(33,150,243,0.3))",
//                 }}
//                 onMouseEnter={() => setImageHover(true)}
//                 onMouseLeave={() => setImageHover(false)}
//               >
//                 <img
//                   src={advPics}
//                   alt="Document Management"
//                   style={{
//                     maxWidth: "70%",
//                     height: "auto",
//                     maxHeight: 180,
//                   }}
//                 />
//               </Box>
//             </Box>
//           </Grid>

//           {/* Right side - Login Form with 3D effect */}
//           <Grid
//             item
//             xs={12}
//             md={6}
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               p: { xs: 2, md: 4 },
//               bgcolor: "white",
//               perspective: "1000px",
//             }}
//           >
//             <Box
//               sx={{
//                 width: "100%",
//                 maxWidth: 400,
//                 p: { xs: 3, md: 4 },
//                 borderRadius: 4,
//                 boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
//                 animation: `${fadeIn} 0.6s ease`,
//                 transform: rightCardHover
//                   ? "rotateY(-2deg) rotateX(1deg) scale(1.02)"
//                   : "rotateY(0) rotateX(0) scale(1)",
//                 transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
//                 bgcolor: "rgba(255,255,255,0.9)",
//                 backdropFilter: "blur(10px)",
//                 border: "1px solid rgba(255,255,255,0.2)",
//                 position: "relative",
//                 '&::after': {
//                   content: '""',
//                   position: "absolute",
//                   top: 0,
//                   left: 0,
//                   right: 0,
//                   bottom: 0,
//                   borderRadius: 4,
//                   background: "linear-gradient(135deg, rgba(33,150,243,0.1), transparent)",
//                   pointerEvents: "none",
//                 },
//               }}
//               onMouseEnter={() => setRightCardHover(true)}
//               onMouseLeave={() => setRightCardHover(false)}
//             >
//               {/* Logo and Title */}
//               <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
//                 <Box
//                   sx={{
//                     width: 70,
//                     height: 70,
//                     borderRadius: "50%",
//                     background: "white",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     boxShadow: "0 10px 20px rgba(33,150,243,0.2)",
//                     mb: 1,
//                   }}
//                 >
//                   <img src={logo} alt="Logo" style={{ height: 50, width: "auto" }} />
//                 </Box>
//                 <Typography
//                   variant="h6"
//                   sx={{
//                     fontWeight: 700,
//                     color: "#0a1929",
//                     animation: `${glow} 3s infinite`,
//                     textAlign: "center",
//                     lineHeight: 1.3,
//                     pb: 1,
//                     fontSize: "1.1rem",
//                   }}
//                 >
//                   Document Management System
//                 </Typography>
//               </Box>

//               <Typography variant="body2" sx={{ color: "#4a5a7a", mb: 3, textAlign: "center", fontSize: "0.85rem" }}>
//                 Sign in to your document workspace
//               </Typography>

//               {isAccountLocked ? (
//                 <LockedScreen />
//               ) : (
//                 <form onSubmit={(e) => e.preventDefault()}>
//                   {/* Username field */}
//                   <Box sx={{ mb: 2 }}>
//                     <Typography 
//                       variant="caption" 
//                       sx={{ 
//                         fontWeight: 500, 
//                         color: isUsernameFocused ? "#2196f3" : "#0a1929", 
//                         mb: 0.5, 
//                         display: "block",
//                         transition: "color 0.2s",
//                       }}
//                     >
//                       Username
//                     </Typography>
//                     <Box
//                       sx={{
//                         display: "flex",
//                         alignItems: "center",
//                         bgcolor: "#f5f7fc",
//                         borderRadius: 2,
//                         px: 2,
//                         py: 1,
//                         border: "2px solid transparent",
//                         transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
//                         "&:focus-within": {
//                           borderColor: "#2196f3",
//                           bgcolor: "white",
//                           boxShadow: "0 0 0 3px rgba(33,150,243,0.2)",
//                           transform: "scale(1.02)",
//                         },
//                       }}
//                     >
//                       <FontAwesomeIcon icon={faUser} style={{ color: "#2196f3", marginRight: 8, fontSize: 14 }} />
//                       <input
//                         type="text"
//                         name="useR_ID"
//                         value={formik.values.useR_ID}
//                         onChange={formik.handleChange}
//                         onBlur={(e) => {
//                           formik.handleBlur(e);
//                           setIsUsernameFocused(false);
//                         }}
//                         onFocus={() => setIsUsernameFocused(true)}
//                         placeholder="Username"
//                         style={{
//                           flex: 1,
//                           border: "none",
//                           outline: "none",
//                           background: "transparent",
//                           fontSize: "0.9rem",
//                           padding: "4px 0",
//                         }}
//                       />
//                     </Box>
//                     {formik.touched.useR_ID && formik.errors.useR_ID && (
//                       <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1, mt: 0.5, display: "block" }}>
//                         ⚠ {formik.errors.useR_ID}
//                       </Typography>
//                     )}
//                   </Box>

//                   {/* Password field */}
//                   <Box sx={{ mb: 1.5 }}>
//                     <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
//                       <Typography 
//                         variant="caption" 
//                         sx={{ 
//                           fontWeight: 500, 
//                           color: isPasswordFocused ? "#2196f3" : "#0a1929",
//                           transition: "color 0.2s",
//                         }}
//                       >
//                         Password
//                       </Typography>
                    
//                     </Box>
//                     <Box
//                       sx={{
//                         display: "flex",
//                         alignItems: "center",
//                         bgcolor: "#f5f7fc",
//                         borderRadius: 2,
//                         px: 2,
//                         py: 1,
//                         border: "2px solid transparent",
//                         transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
//                         "&:focus-within": {
//                           borderColor: "#2196f3",
//                           bgcolor: "white",
//                           boxShadow: "0 0 0 3px rgba(33,150,243,0.2)",
//                           transform: "scale(1.02)",
//                         },
//                       }}
//                     >
//                       <FontAwesomeIcon icon={faLock} style={{ color: "#2196f3", marginRight: 8, fontSize: 14 }} />
//                       <input
//                         type={showPassword ? "text" : "password"}
//                         name="password"
//                         value={formik.values.password}
//                         onChange={formik.handleChange}
//                         onBlur={(e) => {
//                           formik.handleBlur(e);
//                           setIsPasswordFocused(false);
//                         }}
//                         onFocus={() => setIsPasswordFocused(true)}
//                         placeholder="Password"
//                         style={{
//                           flex: 1,
//                           border: "none",
//                           outline: "none",
//                           background: "transparent",
//                           fontSize: "0.9rem",
//                           padding: "4px 0",
//                         }}
//                       />
//                       <FontAwesomeIcon
//                         icon={showPassword ? faEyeSlash : faEye}
//                         style={{ color: "#2196f3", fontSize: 14, cursor: "pointer" }}
//                         onClick={() => setShowPassword(!showPassword)}
//                       />
//                     </Box>
//                     {formik.touched.password && formik.errors.password && (
//                       <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1, mt: 0.5, display: "block" }}>
//                         ⚠ {formik.errors.password}
//                       </Typography>
//                     )}
//                   </Box>

//                   {/* CAPTCHA */}
//                   <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
//                     <ReCAPTCHA
//                       ref={recaptchaRef}
//                       sitekey="6LcBasQrAAAAAFors9vxneugK4N82CUfqhm763YB"
//                       onChange={handleCaptchaChange}
//                       onExpired={handleCaptchaExpired}
//                     />
//                   </Box>

//                   {/* Sign In Button - Blue */}
//                   <Button
//                     type="button"
//                     fullWidth
//                     disabled={loading}
//                     onClick={handleButtonClick}
//                     sx={{
//                       background: "linear-gradient(135deg, #2196f3, #1976d2)",
//                       color: "white",
//                       py: 1.2,
//                       borderRadius: 2,
//                       fontSize: "1rem",
//                       fontWeight: 600,
//                       textTransform: "none",
//                       boxShadow: "0 8px 16px rgba(33,150,243,0.4)",
//                       position: "relative",
//                       overflow: "hidden",
//                       '&::before': {
//                         content: '""',
//                         position: "absolute",
//                         top: 0,
//                         left: "-100%",
//                         width: "100%",
//                         height: "100%",
//                         background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
//                         transition: "left 0.5s ease",
//                       },
//                       "&:hover": {
//                         transform: "translateY(-2px) scale(1.02)",
//                         boxShadow: "0 12px 24px rgba(33,150,243,0.6)",
//                         '&::before': {
//                           left: "100%",
//                         },
//                       },
//                       "&:disabled": { opacity: 0.7 },
//                       transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
//                       mb: 2,
//                     }}
//                   >
//                     {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Sign in →"}
//                   </Button>

//                   {/* Attempts message */}
//                   <AttemptsMessage />
//                 </form>
//               )}
//             </Box>
//           </Grid>
//         </Grid>
//       </Container>
//     </>
//   );
// }

// export default LoginPage;
